import { useState, useCallback } from 'react'
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import { TRowConfigWizard } from './TRowConfigWizard'
import { TRowEditor } from './TRowEditor'
import { useHangar, useUpdateHangar } from '@/hooks/useHangars'
import {
  useHangarUnits,
  useCreateHangarUnits,
  useUpdateHangarUnit,
  useDeleteHangarUnit,
  useDeleteAllHangarUnits,
} from '@/hooks/useHangarUnits'
import { useTenant } from '@/contexts/TenantContext'
import { HANGAR_TYPE_LABELS, type TRowTypeConfig } from '@/types/hangars'
import type { GeneratedUnit, HangarUnitInsert, HangarUnitUpdate } from '@/types/hangar-units'

interface UnitsSheetProps {
  /** Hangar ID to configure units for */
  hangarId: string
  /** Whether the dialog is open */
  open: boolean
  /** Called when open state changes */
  onOpenChange: (open: boolean) => void
}

/**
 * Dedicated dialog for configuring T-hangar units
 * Shows wizard for new units or editor for existing units
 */
export function UnitsSheet({ hangarId, open, onOpenChange }: UnitsSheetProps) {
  const { currentOrganization } = useTenant()
  const [showRegenerateConfirm, setShowRegenerateConfirm] = useState(false)

  // Fetch hangar data for context
  const { data: hangar, isLoading: isLoadingHangar } = useHangar(hangarId)

  // Fetch existing units
  const { data: units = [], isLoading: isLoadingUnits } = useHangarUnits(hangarId)

  // Mutations
  const updateHangar = useUpdateHangar()
  const createUnits = useCreateHangarUnits()
  const updateUnit = useUpdateHangarUnit()
  const deleteUnit = useDeleteHangarUnit()
  const deleteAllUnits = useDeleteAllHangarUnits()

  const isLoading =
    isLoadingHangar ||
    isLoadingUnits ||
    createUnits.isPending ||
    updateUnit.isPending ||
    deleteUnit.isPending ||
    deleteAllUnits.isPending

  const hasUnits = units.length > 0

  // Handle generating new units (from wizard)
  const handleGenerate = useCallback(
    async (config: TRowTypeConfig, generatedUnits: GeneratedUnit[]) => {
      if (!currentOrganization) return

      try {
        // First update hangar with type_config
        await updateHangar.mutateAsync({
          id: hangarId,
          data: {
            type_config: config,
          },
        })

        // Then create the units
        const unitsToCreate: HangarUnitInsert[] = generatedUnits.map((unit) => ({
          building_id: hangarId,
          organization_id: currentOrganization.id,
          unit_code: unit.unit_code,
          sequence: unit.sequence,
          door_width: unit.door_width,
          depth: unit.depth,
          tail_width: unit.tail_width,
          offset_x: unit.offset_x,
          offset_y: unit.offset_y,
          is_leasable: true,
          status: 'available',
        }))

        await createUnits.mutateAsync(unitsToCreate)

        // Close dialog on success
        onOpenChange(false)
      } catch (error) {
        console.error('Error generating units:', error)
      }
    },
    [hangarId, currentOrganization, updateHangar, createUnits, onOpenChange]
  )

  // Handle unit update
  const handleUpdateUnit = useCallback(
    async (id: string, data: HangarUnitUpdate) => {
      try {
        await updateUnit.mutateAsync({ id, hangarId, data })
      } catch (error) {
        console.error('Error updating unit:', error)
      }
    },
    [hangarId, updateUnit]
  )

  // Handle unit delete
  const handleDeleteUnit = useCallback(
    async (id: string) => {
      try {
        await deleteUnit.mutateAsync({ id, hangarId })
      } catch (error) {
        console.error('Error deleting unit:', error)
      }
    },
    [hangarId, deleteUnit]
  )

  // Handle regenerate request (shows confirmation first)
  const handleRegenerateRequest = useCallback(() => {
    setShowRegenerateConfirm(true)
  }, [])

  // Handle regenerate confirmation - delete all then show wizard
  const handleConfirmRegenerate = useCallback(async () => {
    try {
      await deleteAllUnits.mutateAsync(hangarId)
      setShowRegenerateConfirm(false)
    } catch (error) {
      console.error('Error deleting units for regeneration:', error)
    }
  }, [hangarId, deleteAllUnits])

  // Extract type config for editor
  const typeConfig: TRowTypeConfig = (hangar?.type_config as TRowTypeConfig) ?? {
    unit_count: 10,
    nesting_style: 'standard',
    layout: 'single',
    unit_template: {
      door_width: 42,
      depth: 32,
      tail_width: 24,
    },
    shared_taxilane_width: 50,
  }

  return (
    <>
      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent className="sm:max-w-4xl max-h-[90vh] overflow-hidden flex flex-col p-0">
          <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0">
            <DialogTitle>
              Configure Units - {hangar?.name ?? 'Loading...'}
            </DialogTitle>
            <DialogDescription>
              {hangar && (
                <>
                  {HANGAR_TYPE_LABELS[hangar.type]} • {hangar.exterior_width}×
                  {hangar.exterior_depth} ft
                </>
              )}
            </DialogDescription>
          </DialogHeader>

          <div className="flex-1 overflow-y-auto p-6">
            {isLoadingHangar || isLoadingUnits ? (
              <div className="flex items-center justify-center h-40">
                <p className="text-muted-foreground">Loading...</p>
              </div>
            ) : hasUnits ? (
              // Show editor for existing units
              <TRowEditor
                units={units}
                typeConfig={typeConfig}
                parentPricingModel={hangar?.pricing_model ?? 'fixed'}
                parentMonthlyRate={hangar?.monthly_rate_private}
                parentRatePerSqft={hangar?.rate_per_sqft_private}
                onUpdateUnit={handleUpdateUnit}
                onDeleteUnit={handleDeleteUnit}
                onRegenerate={handleRegenerateRequest}
                isLoading={isLoading}
              />
            ) : (
              // Show wizard to generate new units
              <TRowConfigWizard
                onGenerate={handleGenerate}
                onCancel={() => onOpenChange(false)}
                initialConfig={typeConfig}
                isLoading={createUnits.isPending}
              />
            )}
          </div>
        </DialogContent>
      </Dialog>

      {/* Regenerate Confirmation Dialog */}
      <AlertDialog open={showRegenerateConfirm} onOpenChange={setShowRegenerateConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Regenerate all units?</AlertDialogTitle>
            <AlertDialogDescription>
              This will delete all {units.length} existing units and let you configure new ones.
              Any custom pricing or status changes will be lost.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleConfirmRegenerate}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Delete & Regenerate
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </>
  )
}
